import { useState } from 'react';
import { notify } from '@/lib/notify';
import { Link, useNavigate } from 'react-router-dom';
import { createCoupon } from '@/lib/promotions';
import { humanize } from '@/lib/errors';

/**
 * /admin/promotions/new — create a coupon. Edit-mode is deferred (Stripe
 * coupons are immutable on most fields once created — better UX is
 * "deactivate + create new").
 */
export default function PromotionEdit() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState<'percent'|'amount_cad'>('percent');
  const [discountValue, setDiscountValue] = useState<string>('20');
  const [duration, setDuration] = useState<'once'|'repeating'|'forever'>('repeating');
  const [durationInMonths, setDurationInMonths] = useState<string>('3');
  const [maxRedemptions, setMaxRedemptions] = useState<string>('');
  const [redeemBy, setRedeemBy] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setErr(null);
    try {
      // Toast fires after the await resolves (see below)
      await createCoupon({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        discountType,
        discountValue: Number(discountValue),
        duration,
        durationInMonths: duration === 'repeating' ? Number(durationInMonths) : null,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
        redeemByIso: redeemBy ? new Date(redeemBy + 'T23:59:59Z').toISOString() : null,
      });
      notify.success('Promotion created');
      navigate('/admin/promotions');
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link to="/admin/promotions" className="text-xs underline text-muted-foreground">← All promotions</Link>
        <h2 className="text-2xl font-semibold tracking-tight">New coupon</h2>
        <p className="text-sm text-muted-foreground">
          Pre-Stripe: this row stores the discount template. Apply to a specific subscription via the per-org editor on /admin/subscriptions/&lt;org&gt;. Post-Stripe: this becomes a Stripe Coupon + Promotion Code customers can redeem.
        </p>
      </header>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3 max-w-2xl">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Code (e.g. BLACK40)">
            <input value={code} onChange={(e)=>setCode(e.target.value)} className="input" placeholder="BLACK40" />
          </Field>
          <Field label="Internal name">
            <input value={name} onChange={(e)=>setName(e.target.value)} className="input" placeholder="Black Friday 2026" />
          </Field>
          <Field label="Discount type">
            <select value={discountType} onChange={(e)=>setDiscountType(e.target.value as 'percent' | 'amount_cad')} className="input">
              <option value="percent">Percent off</option>
              <option value="amount_cad">Amount off (CAD)</option>
            </select>
          </Field>
          <Field label={discountType === 'percent' ? 'Percent (1..100)' : 'Amount CAD'}>
            <input type="number" min={1} step="0.01" value={discountValue} onChange={(e)=>setDiscountValue(e.target.value)} className="input" />
          </Field>
          <Field label="Duration">
            <select value={duration} onChange={(e)=>setDuration(e.target.value as 'once' | 'repeating' | 'forever')} className="input">
              <option value="once">Once (single billing period)</option>
              <option value="repeating">Repeating (N months)</option>
              <option value="forever">Forever</option>
            </select>
          </Field>
          {duration === 'repeating' && (
            <Field label="Months">
              <input type="number" min={1} value={durationInMonths} onChange={(e)=>setDurationInMonths(e.target.value)} className="input" />
            </Field>
          )}
          <Field label="Max redemptions (blank = unlimited)">
            <input type="number" min={1} value={maxRedemptions} onChange={(e)=>setMaxRedemptions(e.target.value)} className="input" />
          </Field>
          <Field label="Expires (blank = no expiry)">
            <input type="date" value={redeemBy} onChange={(e)=>setRedeemBy(e.target.value)} className="input" />
          </Field>
        </div>

        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button disabled={busy || !code || !name} onClick={submit} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {busy ? 'Creating…' : 'Create coupon'}
          </button>
          <Link to="/admin/promotions" className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
            Cancel
          </Link>
        </div>
      </section>

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));border-radius:6px;padding:0.375rem 0.5rem;font-size:0.875rem;background:transparent}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs space-y-1">
      <span className="text-muted-foreground uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
